import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import api from '@/store/api';
import { athena, unity } from '@/store/api/instances';
import type { Coupon, CreateCouponRequest, RawCoupon, UpdateCouponRequest } from '@/types';

export const couponsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCoupons: build.query<ApiResponse<Coupon[]>, ApiListingRequest>({
      query: (data) => athena({ url: 'v2/coupons', method: 'POST', data }),
      transformResponse: (response: ApiResponse<RawCoupon[]>) => ({
        ...response,
        data: (response?.data ?? []).map((coupon) => ({ ...coupon, id: coupon.coupon_id })),
      }),
      providesTags: ['Coupons'],
    }),
    getCoupon: build.query<ApiResponse<Coupon>, number>({
      query: (couponId) => athena({ url: `v2/coupons/${couponId}`, method: 'GET' }),
      transformResponse: (response: ApiResponse<RawCoupon>) => ({
        ...response,
        data: response?.data
          ? { ...response.data, id: response.data.coupon_id }
          : (response?.data as unknown as Coupon),
      }),
      providesTags: (_, __, couponId) => [{ type: 'Coupons', id: couponId }],
    }),
    createCoupon: build.mutation<ApiResponse<Coupon>, CreateCouponRequest>({
      query: ({ file, ...data }) => {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(data));
        if (file) {
          formData.append('file', file);
        }

        return unity({
          url: 'coupons/create',
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      },
      invalidatesTags: ['Coupons'],
    }),
    // Partial update — immutable coupon attributes (code, discount, coupon_type, customer
    // assignment) aren't accepted here; see UpdateCouponRequest.
    updateCoupon: build.mutation<ApiResponse<Coupon>, { couponId: number } & UpdateCouponRequest>({
      query: ({ couponId, file, ...data }) => {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(data));
        if (file) {
          formData.append('file', file);
        }

        return unity({
          url: `coupons/${couponId}`,
          method: 'PUT',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      },
      invalidatesTags: (_, __, { couponId }) => [{ type: 'Coupons', id: couponId }, 'Coupons'],
    }),
  }),
});

export const {
  useGetCouponsQuery,
  useGetCouponQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
} = couponsApi;
